import useDebounceCallback from "@base/model/hooks/useDebounceCallback";
import { LOCAL_DRAFT_DEBOUNCE_MS, SERVER_DRAFT_DEBOUNCE_MS } from "@features/draft/model/constants";
import { useStore } from "@tanstack/react-form";
import { useEffect, useRef } from "react";
import type { DailyLogForm } from "./form";
import type { DailyLogFormValues } from "./schema";

interface UseAutoSaveOptions {
  onLocalSave: (values: DailyLogFormValues) => void;
  onServerSave: (values: DailyLogFormValues) => void;
}

export function useAutoSave(form: DailyLogForm, { onLocalSave, onServerSave }: UseAutoSaveOptions) {
  const isDirty = useStore(form.store, (state) => state.isDirty);
  const formValues = useStore(form.store, (state) => state.values);

  const onLocalSaveRef = useRef(onLocalSave);
  onLocalSaveRef.current = onLocalSave;

  const onServerSaveRef = useRef(onServerSave);
  onServerSaveRef.current = onServerSave;

  const lastLocalHashRef = useRef("");
  const lastServerHashRef = useRef("");

  const debouncedLocalSave = useDebounceCallback((values: DailyLogFormValues) => {
    const hash = JSON.stringify(values);
    if (hash === lastLocalHashRef.current) return;
    lastLocalHashRef.current = hash;
    onLocalSaveRef.current(values);
  }, LOCAL_DRAFT_DEBOUNCE_MS);

  const debouncedServerSave = useDebounceCallback((values: DailyLogFormValues) => {
    const hash = JSON.stringify(values);
    if (hash === lastServerHashRef.current) return;
    lastServerHashRef.current = hash;
    onServerSaveRef.current(values);
  }, SERVER_DRAFT_DEBOUNCE_MS);

  useEffect(() => {
    if (isDirty) {
      debouncedLocalSave(formValues);
      debouncedServerSave(formValues);
    }
  }, [isDirty, formValues, debouncedLocalSave, debouncedServerSave]);

  useEffect(() => {
    const handleOnline = () => {
      const hash = JSON.stringify(formValues);
      if (hash === lastServerHashRef.current) return;
      lastServerHashRef.current = hash;
      onServerSaveRef.current(formValues);
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [formValues]);
}
